import { inject, Injectable } from '@angular/core';
import { capitalize } from 'lodash-es';
import type {
    Article as IArticle,
    BreadcrumbList as IBreadcrumbList,
    CreativeWork as ICreativeWork,
    FAQPage as IFAQPage,
    ImageObject as IImageObject,
    Organization as IOrganization,
    Person as IPerson,
    Product as IProduct,
    Question as IQuestion,
    VideoObject as IVideoObject,
    WebSite as IWebSite,
    WithContext,
} from 'schema-dts';

import type { IBreadcrumbItem } from 'components/atoms/breadcrumbs/entities/types';
import type { ICustomerSlide } from 'components/molecules/customer-quote-with-big-numbers/entities/types';
import { defaultLogoLightPaths } from 'components/organisms/header-v2/partials/header-v2-logo/entities/const';
import { EnvironmentService } from 'src/services/environment/environment.service';
import type { IArticleData } from 'src/shared/types/article';
import type { TFaq } from 'src/shared/types/faq';
import { EProductType } from 'src/shared/types/product-type';
import type { IRating } from 'src/shared/types/rating';
import type { TVideo } from 'src/shared/types/video';

@Injectable({
    providedIn: 'root',
})
export class JsonLdService {
    private readonly envService = inject(EnvironmentService);

    public get organization(): {
        name: string;
        url: string;
        logo: string;
    } {
        const product = capitalize(this.envService.environment.productType);
        const url =
            this.envService.environment.productType === EProductType.Wrike
                ? 'https://www.wrike.com/'
                : 'https://klaxoon.com/';
        return {
            name: product,
            url: url,
            logo: defaultLogoLightPaths[this.envService.environment.productType],
        };
    }

    public generateFaqPage(faqs: TFaq[] | null): WithContext<IFAQPage> | void {
        if (!faqs || faqs?.length < 1) {
            return;
        }

        const schemaQuestions = [];

        faqs.map((question) => {
            const schemaQuestion = this.generateQuestion(question);

            if (schemaQuestion) {
                schemaQuestions.push(schemaQuestion);
            }
        });

        if (schemaQuestions.length < 1) {
            return;
        }

        return this.sanitizeSchema({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: schemaQuestions,
        });
    }

    public generateQuestion(faq: TFaq): IQuestion | undefined {
        const questionText = faq.question?.text || null;
        const hasQA = questionText != null && faq.answer !== null;

        if (!hasQA) {
            return undefined;
        }

        return this.sanitizeSchema({
            '@type': 'Question',
            name: questionText,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
            },
        });
    }

    public generateVideo(video: TVideo): WithContext<IVideoObject> | void {
        if (
            !video.title ||
            !video.description ||
            !video.previewUrl ||
            !video.duration ||
            !video.uploadDate ||
            !video.contentUrl ||
            !video.embedUrl
        ) {
            return;
        }

        return this.sanitizeSchema({
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            thumbnailUrl: video.previewUrl,
            name: video.title,
            description: video.description,
            uploadDate: video.uploadDate,
            duration: video.duration,
            contentUrl: video.contentUrl,
            embedUrl: video.embedUrl,
        });
    }

    public generateArticle(data: IArticleData): WithContext<IArticle> | void {
        const defaultImage =
            'https://cdn.wrike.com/image/__cdnu/illustration_abstract_rebrand-meta-pic_shapes_electric.png';

        const defaultAuthor = [
            {
                '@type': 'Organization',
                name: this.organization.name,
                url: this.organization.url,
            },
        ];

        const { image, headline, datePublished, dateModified, author, description, mainEntityOfPage } = data;

        const authors = author ? author : defaultAuthor;

        return this.sanitizeSchema({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline,
            image: image ? image : defaultImage,
            datePublished,
            dateModified,
            author: authors as IPerson[],
            publisher: {
                '@type': 'Organization',
                name: this.organization.name,
                logo: {
                    '@type': 'ImageObject',
                    url: this.organization.logo,
                    width: '600',
                    height: '60',
                },
            },
            description,
            mainEntityOfPage,
        });
    }

    public generateBreadcrumbs(breadcrumbs: IBreadcrumbItem[]): WithContext<IBreadcrumbList> | void {
        if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
            return;
        }

        const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.wrike.com/';
        const filteredBreadcrumbs = breadcrumbs.filter((item) => item?.link);

        const itemListElement: IBreadcrumbList['itemListElement'] = filteredBreadcrumbs.map(({ text, link }, index) => {
            const absoluteLink = link?.startsWith('http') ? link : `${origin}${link}`;
            const name = text || link;

            return {
                '@type': 'ListItem',
                position: 1 + index,
                item: {
                    '@id': absoluteLink,
                    name,
                },
            };
        });
        return this.sanitizeSchema({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement,
        });
    }

    public generateOrganisation({
        socialMediaLinks,
        logoUrl,
    }: {
        socialMediaLinks: string[];
        logoUrl?: string;
    }): WithContext<IOrganization> {
        return this.sanitizeSchema({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            url: this.organization.url,
            logo: logoUrl || this.organization.logo,
            sameAs: socialMediaLinks,
        });
    }

    public generateWebSite(): WithContext<IWebSite> {
        return this.sanitizeSchema({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: this.organization.name,
            url: this.organization.url,
        });
    }

    public generateAggregateRating(rating: IRating): WithContext<IProduct> | void {
        const { ratingCount, worstRating, bestRating, ratingValue, description, image } = rating;

        return this.sanitizeSchema({
            '@context': 'https://schema.org',
            '@type': 'Product',
            '@id': 'https://www.wrike.com/tour',
            name: this.organization.name,
            image: this.organization.logo,
            description,
            brand: {
                '@type': 'Brand',
                name: this.organization.name,
            },
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue,
                bestRating,
                worstRating,
                ratingCount: Number(ratingCount),
            },
        });
    }

    public generateImageObject({
        url,
        width,
        height,
        caption,
    }: {
        url: string;
        width: string;
        height: string;
        caption: string;
    }): WithContext<IImageObject> {
        return this.sanitizeSchema({
            '@context': 'https://schema.org',
            '@type': 'ImageObject',
            author: {
                '@type': 'Organization',
                name: this.organization.name,
                url: this.organization.url,
            },
            url,
            contentUrl: url,
            width,
            height,
            caption,
        });
    }

    public generateQuote(quote: Partial<ICustomerSlide['quotesPreset']>): WithContext<ICreativeWork> {
        const { quoteText = '', author, authorJobTitle, photo2x, createdAt } = quote;

        return this.sanitizeSchema({
            '@context': 'https://schema.org',
            '@type': 'CreativeWork',
            citation: this.stripHtml(quoteText),
            author: {
                '@type': 'Person',
                name: author,
                jobTitle: authorJobTitle,
                image: {
                    '@type': 'ImageObject',
                    url: photo2x?.src,
                    width: '60',
                    height: '60',
                },
            },
            dateCreated: createdAt,
        });
    }

    public sanitizeSchema<T>(schema: T): T {
        return this.sanitizeSchemaValue(schema) as T;
    }

    public stripHtml(html = '', { removeQuotes = true }: { removeQuotes?: boolean } = {}): string {
        const text = html
            .replace(/<\/?[^>]+(>|$)/g, ' ') // replace HTML tags with space (e.g. <p>, <br>, <ul>, <li>)
            .replace(/&nbsp;/g, ' ') // convert non-breaking spaces to regular spaces
            .replace(/\s+/g, ' ') // collapse whitespace (spaces/tabs/newlines) into a single space
            .replace(/ ([.,!?;:])/g, '$1') // drop space inserted before punctuation by tag removal
            .trim(); // remove leading/trailing whitespace

        // Quotes are stripped for citation-like fields; FAQ keeps apostrophes/quotes as content.
        return removeQuotes ? text.replace(/[”'"“]/g, '') : text;
    }

    /** Plain text for JSON-LD fields; preserves quotes/apostrophes (unlike citation stripHtml default). */
    private toPlainText(value: string | null | undefined): string | undefined {
        if (value == null) {
            return value as undefined;
        }

        // Avoid rewriting plain text (e.g. video chapter newlines) when there is no markup.
        if (!/<[^>]+>/.test(value) && !value.includes('&nbsp;')) {
            return value;
        }

        return this.stripHtml(value, { removeQuotes: false });
    }

    private sanitizeSchemaValue(value: unknown, key?: string): unknown {
        const urlKeys = new Set([
            '@context',
            '@id',
            'url',
            'contentUrl',
            'embedUrl',
            'thumbnailUrl',
            'sameAs',
            'mainEntityOfPage',
            'logo',
            'image',
        ]);

        if (typeof value === 'string') {
            return urlKeys.has(key ?? '') ? value : this.toPlainText(value);
        }

        if (Array.isArray(value)) {
            return value.map((item) => this.sanitizeSchemaValue(item, key));
        }

        if (value && typeof value === 'object') {
            return Object.fromEntries(
                Object.entries(value).map(([childKey, childValue]) => [
                    childKey,
                    this.sanitizeSchemaValue(childValue, childKey),
                ])
            );
        }

        return value;
    }
}
